/**
 * QuoteUploadWizard — multi-PDF upload wizard for insurance quotes.
 *
 * Steps:
 *   drop    — add more PDFs (always accessible via "Add More" button)
 *   form    — per-PDF review/fill tabs (shown immediately if existing quotes exist)
 *   preview — WhatsApp message preview with copy button
 *
 * Previously uploaded DRAFT quotes are loaded on mount and shown as tabs
 * alongside any new PDFs added in the current session.
 */
import { useState, useRef, useCallback, useEffect } from 'react'
import { X, Upload, Copy, Check, AlertTriangle, ChevronLeft, ChevronRight, FileText, Plus, Send } from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { leadService } from '../../leads/services/leads'
import { authService } from '../../identity/services/auth'
import { useWindowWidth } from '../../../lib/useWindowWidth'
import type { Quote, QuoteExtractionResult } from '../../../types'

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_FILES = 5
const CONFIDENCE_WARN = 0.85

const POLICY_TYPES = [
  { value: 'COMPREHENSIVE', label: 'Comprehensive' },
  { value: 'THIRD_PARTY', label: 'Third Party' },
  { value: 'OD_ONLY', label: 'OD Only' },
]

const ADDON_FIELDS: { key: keyof AddonFlags; label: string }[] = [
  { key: 'zero_depreciation', label: 'Zero Depreciation' },
  { key: 'roadside_assistance', label: 'Roadside Assistance (RSA)' },
  { key: 'engine_protection', label: 'Engine Protection' },
  { key: 'consumables_cover', label: 'Consumables Cover' },
  { key: 'ncb_protection', label: 'NCB Protection' },
  { key: 'key_replacement', label: 'Key Replacement' },
]

// ── Types ─────────────────────────────────────────────────────────────────────

interface AddonFlags {
  zero_depreciation: boolean
  roadside_assistance: boolean
  engine_protection: boolean
  consumables_cover: boolean
  ncb_protection: boolean
  key_replacement: boolean
}

interface QuoteFormState extends AddonFlags {
  insurer_name: string
  policy_type: string
  premium_amount: string
  idv: string
  od_premium: string
  tp_premium: string
}

interface SlotState {
  file: File | null          // null for quotes loaded from DB (no File object available)
  quote: Quote | null
  form: QuoteFormState
  extraction: QuoteExtractionResult | null
  uploading: boolean
  extracting: boolean
  error: string | null
}

interface Props {
  leadId: number
  customerName: string
  vehicleReg?: string
  onClose: () => void
  onQuotesUploaded: () => void
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function emptyForm(): QuoteFormState {
  return {
    insurer_name: '',
    policy_type: '',
    premium_amount: '',
    idv: '',
    od_premium: '',
    tp_premium: '',
    zero_depreciation: false,
    roadside_assistance: false,
    engine_protection: false,
    consumables_cover: false,
    ncb_protection: false,
    key_replacement: false,
  }
}

function formFromQuote(q: Quote): QuoteFormState {
  return {
    insurer_name: q.insurer_name ?? '',
    policy_type: q.policy_type ?? '',
    premium_amount: q.premium_amount != null ? String(q.premium_amount) : '',
    idv: q.idv != null ? String(q.idv) : '',
    od_premium: q.od_premium != null ? String(q.od_premium) : '',
    tp_premium: q.tp_premium != null ? String(q.tp_premium) : '',
    zero_depreciation: q.zero_depreciation,
    roadside_assistance: q.roadside_assistance,
    engine_protection: q.engine_protection,
    consumables_cover: q.consumables_cover,
    ncb_protection: q.ncb_protection,
    key_replacement: q.key_replacement,
  }
}

function applyExtraction(form: QuoteFormState, ex: QuoteExtractionResult): QuoteFormState {
  return {
    insurer_name: ex.insurer_name ?? form.insurer_name,
    policy_type: ex.policy_type ?? form.policy_type,
    premium_amount: ex.premium_amount != null ? String(ex.premium_amount) : form.premium_amount,
    idv: ex.idv != null ? String(ex.idv) : form.idv,
    od_premium: ex.od_premium != null ? String(ex.od_premium) : form.od_premium,
    tp_premium: ex.tp_premium != null ? String(ex.tp_premium) : form.tp_premium,
    zero_depreciation: ex.zero_depreciation,
    roadside_assistance: ex.roadside_assistance,
    engine_protection: ex.engine_protection,
    consumables_cover: ex.consumables_cover,
    ncb_protection: ex.ncb_protection,
    key_replacement: ex.key_replacement,
  }
}

function ConfidenceBadge({ score }: { score: number }) {
  if (score >= CONFIDENCE_WARN) return null
  return (
    <span
      title={`AI confidence: ${Math.round(score * 100)}%`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        fontSize: '11px',
        padding: '1px 6px',
        borderRadius: '999px',
        background: 'var(--warning-bg, #fef3c7)',
        color: 'var(--warning-text, #92400e)',
        marginLeft: '6px',
      }}
    >
      <AlertTriangle style={{ width: 10, height: 10 }} />
      {Math.round(score * 100)}%
    </span>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function QuoteUploadWizard({ leadId, customerName, vehicleReg, onClose, onQuotesUploaded }: Props) {
  const isMobile = useWindowWidth() < 640
  const [step, setStep] = useState<'drop' | 'processing' | 'form' | 'preview'>('drop')
  const [activeTab, setActiveTab] = useState(0)
  const [slots, setSlots] = useState<SlotState[]>([])
  const [aiEnabled, setAiEnabled] = useState(false)
  const [comparisonCount, setComparisonCount] = useState(2)
  const [loadingExisting, setLoadingExisting] = useState(true)
  const [waMessage, setWaMessage] = useState<string | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [sendingTemplate, setSendingTemplate] = useState(false)
  const [templateSent, setTemplateSent] = useState(false)
  const [copied, setCopied] = useState(false)
  const [dropError, setDropError] = useState<string | null>(null)
  const [showValidation, setShowValidation] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch fresh user flags + existing quotes on mount (parallel)
  useEffect(() => {
    Promise.all([
      authService.fetchMe().then(user => {
        setAiEnabled(!!user.tenant_feature_flags?.['ai_extraction_enabled'])
        setComparisonCount(Number(user.tenant_feature_flags?.['quote_comparison_count'] ?? 2))
      }).catch(() => {}),
      leadService.listQuotes(leadId),
    ]).then(([, quotes]) => {
      const draft = (quotes as Quote[]).filter(q => q.status === 'DRAFT')
      if (draft.length > 0) {
        setSlots(draft.map(q => ({
          file: null,
          quote: q,
          form: formFromQuote(q),
          extraction: null,
          uploading: false,
          extracting: false,
          error: null,
        })))
        setStep('form')
        setActiveTab(0)
      }
    }).catch(() => {}).finally(() => setLoadingExisting(false))
  }, [leadId])

  const updateSlot = useCallback((index: number, patch: Partial<SlotState>) => {
    setSlots(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s))
  }, [])

  const updateForm = useCallback((index: number, patch: Partial<QuoteFormState>) => {
    setSlots(prev => prev.map((s, i) => i === index ? { ...s, form: { ...s.form, ...patch } } : s))
  }, [])

  // ── Drop zone ────────────────────────────────────────────────────────────────

  const handleFiles = (files: FileList | null) => {
    if (!files) return
    const pdfs = Array.from(files).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'))
    if (pdfs.length === 0) { setDropError('Please select PDF files only.'); return }

    const remaining = MAX_FILES - slots.length
    if (remaining <= 0) {
      setDropError(`Maximum ${MAX_FILES} quotes reached. Delete an existing quote to add more.`)
      return
    }
    if (pdfs.length > remaining) {
      setDropError(`Only ${remaining} more PDF(s) can be added (max ${MAX_FILES} total). Using the first ${remaining}.`)
    } else {
      setDropError(null)
    }
    const selected = pdfs.slice(0, remaining)
    setSlots(prev => [
      ...prev,
      ...selected.map(file => ({
        file,
        quote: null,
        form: emptyForm(),
        extraction: null,
        uploading: false,
        extracting: false,
        error: null,
      })),
    ])
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    handleFiles(e.dataTransfer.files)
  }

  const removeNewFile = (index: number) => {
    setSlots(prev => prev.filter((_, i) => i !== index))
    setDropError(null)
  }

  const uploadNewAndGoToForm = async () => {
    const newSlots = slots.filter(s => s.file && !s.quote)
    if (newSlots.length === 0) { setStep('form'); return }

    setStep('processing')

    await Promise.all(slots.map(async (slot, i) => {
      if (!slot.file || slot.quote) return  // skip already-uploaded slots
      updateSlot(i, { uploading: true, error: null })
      try {
        const quote = await leadService.uploadQuote(leadId, slot.file, { deferred: true, sort_order: i })
        updateSlot(i, { quote, uploading: false })

        if (aiEnabled) {
          updateSlot(i, { extracting: true })
          try {
            const extraction = await leadService.extractQuote(leadId, quote.id)
            setSlots(prev => prev.map((s, idx) =>
              idx === i
                ? { ...s, extraction, extracting: false, form: applyExtraction(s.form, extraction) }
                : s
            ))
          } catch {
            updateSlot(i, { extracting: false })
          }
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Upload failed'
        updateSlot(i, { uploading: false, error: msg })
      }
    }))

    setStep('form')
    // Focus the first newly uploaded tab
    const firstNew = slots.findIndex(s => s.file && !s.quote)
    setActiveTab(firstNew >= 0 ? firstNew : slots.length - 1)
  }

  // ── Delete slot ───────────────────────────────────────────────────────────────

  const deleteSlot = async (index: number) => {
    const slot = slots[index]
    if (slot.quote) {
      try { await leadService.deleteQuote(leadId, slot.quote.id) } catch { /* best effort */ }
    }
    setSlots(prev => {
      const next = prev.filter((_, i) => i !== index)
      if (next.length === 0) setStep('drop')
      return next
    })
    setActiveTab(t => Math.max(0, Math.min(t, slots.length - 2)))
  }

  // ── Preview ───────────────────────────────────────────────────────────────────

  const goToPreview = async () => {
    setShowValidation(true)
    if (!allSlotsValid) return
    setLoadingPreview(true)
    try {
      await Promise.all(slots.map(async (slot) => {
        if (!slot.quote) return
        const f = slot.form
        await leadService.updateQuote(leadId, slot.quote.id, {
          insurer_name: f.insurer_name || undefined,
          policy_type: f.policy_type || undefined,
          premium_amount: f.premium_amount ? parseFloat(f.premium_amount) : undefined,
          idv: f.idv ? parseFloat(f.idv) : undefined,
          od_premium: f.od_premium ? parseFloat(f.od_premium) : undefined,
          tp_premium: f.tp_premium ? parseFloat(f.tp_premium) : undefined,
          zero_depreciation: f.zero_depreciation,
          roadside_assistance: f.roadside_assistance,
          engine_protection: f.engine_protection,
          consumables_cover: f.consumables_cover,
          ncb_protection: f.ncb_protection,
          key_replacement: f.key_replacement,
        })
      }))
      const ADDON_LABELS: Record<string, string> = {
        zero_depreciation: 'Zero Dep',
        roadside_assistance: 'RSA',
        engine_protection: 'Engine Protect',
        consumables_cover: 'Consumables',
        ncb_protection: 'NCB Protect',
        key_replacement: 'Key Replace',
      }
      const fmtPremium = (v: string) => `Rs. ${parseInt(v, 10).toLocaleString('en-IN')}`
      const fmtIdv     = (v: string) => `Rs. ${parseInt(v, 10).toLocaleString('en-IN')}`
      const fmtAddons  = (f: QuoteFormState) => {
        const labels = Object.entries(ADDON_LABELS)
          .filter(([key]) => (f as unknown as Record<string, unknown>)[key])
          .map(([, label]) => label)
        return labels.length ? labels.join(', ') : 'None'
      }

      // Preserve upload order (matches sort_order used by the quote list and
      // the actual "Send to Customer" WhatsApp send) — do NOT re-sort by
      // premium, or Option numbering here would disagree with everywhere else.
      const sorted = slots

      // Single message format regardless of quote count — previously 2 quotes
      // used a "Dear customer / Option N" format while 3+ used a differently
      // styled "*Insurance Quote Comparison*" numbered-list format, so the
      // message a customer received looked inconsistent from one lead to the
      // next depending only on how many PDFs happened to be uploaded.
      const lines = [
        `Dear ${customerName || 'Valued Customer'},`,
        '',
        `As per your request, please find below the options for your vehicle insurance for ${vehicleReg || 'N/A'}`,
        '',
      ]
      sorted.forEach((s, i) => {
        lines.push(`Option ${i + 1}:`)
        lines.push(`Insurer: ${s.form.insurer_name}`)
        lines.push(`IDV: ${fmtIdv(s.form.idv)}`)
        lines.push(`Premium: ${fmtPremium(s.form.premium_amount)}`)
        lines.push(`Add-ons: ${fmtAddons(s.form)}`)
        lines.push('')
      })
      lines.push('Please let us know your preference.')
      const preview = lines.join('\n')

      setWaMessage(preview)
      setStep('preview')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate preview'
      alert(msg)
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleCopy = () => {
    if (!waMessage) return
    navigator.clipboard.writeText(waMessage).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // ── Validation ────────────────────────────────────────────────────────────────

  const slotErrors = (slot: SlotState): string[] => {
    const errs: string[] = []
    if (!slot.form.insurer_name.trim()) errs.push('Insurer Name is required')
    if (!slot.form.policy_type) errs.push('Policy Type is required')
    if (!slot.form.premium_amount || parseFloat(slot.form.premium_amount) <= 0) errs.push('Premium is required')
    if (!slot.form.idv || parseFloat(slot.form.idv) <= 0) errs.push('IDV is required')
    return errs
  }

  const allSlotsValid = slots.length > 0 && slots.every(s => slotErrors(s).length === 0)

  // ── Derived state ─────────────────────────────────────────────────────────────

  const busy = slots.some(s => s.uploading || s.extracting)
  const newPending = slots.filter(s => s.file && !s.quote)
  const allUploaded = slots.length > 0 && slots.every(s => s.quote !== null)

  if (loadingExisting) {
    return (
      <ModalOverlay onClose={onClose}>
        <div className="modal-panel" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
          <div className="p-8 text-center">
            <span className="spinner" style={{ width: 24, height: 24, display: 'inline-block' }} />
            <p style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 14 }}>Loading quotes…</p>
          </div>
        </div>
      </ModalOverlay>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <ModalOverlay onClose={onClose}>
      <div
        className="modal-panel"
        style={{ maxWidth: '700px', width: '100%', padding: 0 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{ padding: isMobile ? '16px 16px 12px' : '20px 24px 16px', marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            <h2 style={{ fontSize: isMobile ? 15 : 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              Upload Quotes{customerName ? ` — ${customerName}` : ''}
            </h2>
            {!loadingExisting && !isMobile && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 999,
                  flexShrink: 0,
                  background: aiEnabled ? 'var(--success-bg, #dcfce7)' : 'var(--bg-muted)',
                  color: aiEnabled ? 'var(--success-text, #166534)' : 'var(--text-secondary)',
                  border: `1px solid ${aiEnabled ? 'var(--success-border, #86efac)' : 'var(--border-color)'}`,
                  letterSpacing: '0.02em',
                }}
              >
                {aiEnabled ? '✦ AI Extraction ON' : 'AI Extraction OFF'}
              </span>
            )}
          </div>
          <button className="btn-icon" style={{ flexShrink: 0, marginLeft: 8 }} onClick={onClose} disabled={busy}><X className="h-4 w-4" /></button>
        </div>

        {/* Step pills */}
        <div style={{ display: 'flex', gap: 4, padding: isMobile ? '6px 16px' : '8px 24px', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
          {[
            { key: 'drop', label: isMobile ? '1. PDFs' : '1. Add PDFs' },
            { key: 'form', label: isMobile ? '2. Review' : '2. Review Fields' },
            { key: 'preview', label: isMobile ? '3. Preview' : '3. WA Preview' },
          ].map(({ key, label }) => (
            <span
              key={key}
              style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 999,
                background: step === key || (step === 'processing' && key === 'form')
                  ? 'var(--brand-primary)' : 'var(--bg-muted)',
                color: step === key || (step === 'processing' && key === 'form')
                  ? '#fff' : 'var(--text-secondary)',
                fontWeight: step === key || (step === 'processing' && key === 'form') ? 600 : 400,
              }}
            >
              {label}
            </span>
          ))}
        </div>

        <div style={{ padding: isMobile ? '16px' : '20px 24px' }}>

          {/* ── Drop zone ──────────────────────────────────────────────────── */}
          {step === 'drop' && (
            <div className="space-y-4">
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--border-color)',
                  borderRadius: 8,
                  padding: '40px 20px',
                  textAlign: 'center',
                  cursor: slots.length >= MAX_FILES ? 'not-allowed' : 'pointer',
                  background: 'var(--bg-muted)',
                  opacity: slots.length >= MAX_FILES ? 0.5 : 1,
                }}
              >
                <Upload style={{ width: 32, height: 32, margin: '0 auto 8px', color: 'var(--text-secondary)' }} />
                <p style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: 4 }}>
                  Drop quote PDFs here or click to select
                </p>
                <p className="form-hint" style={{ margin: 0 }}>
                  {slots.length > 0
                    ? `${slots.length} of ${MAX_FILES} slots used — ${MAX_FILES - slots.length} more allowed`
                    : `Up to ${MAX_FILES} PDFs • Each PDF = one insurer quote`}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => handleFiles(e.target.files)}
                  disabled={slots.length >= MAX_FILES}
                />
              </div>

              {dropError && <p className="alert-warning" style={{ margin: 0, fontSize: 13 }}>{dropError}</p>}

              {/* New files pending upload */}
              {newPending.length > 0 && (
                <div className="space-y-2">
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>New files to upload:</p>
                  {newPending.map((s, i) => {
                    const globalIdx = slots.indexOf(s)
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FileText style={{ width: 16, height: 16, color: 'var(--text-secondary)', flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.file!.name}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          {(s.file!.size / 1024).toFixed(0)} KB
                        </span>
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                          onClick={e => { e.stopPropagation(); removeNewFile(globalIdx) }}
                        >
                          <X style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}

              {aiEnabled && newPending.length > 0 && (
                <p className="alert-info" style={{ margin: 0, fontSize: 13 }}>
                  AI extraction is enabled — fields will be pre-populated from each PDF.
                </p>
              )}

              <div className="flex justify-between pt-2" style={{ gap: 8 }}>
                {slots.length > 0 ? (
                  <button className="btn-secondary" onClick={() => setStep('form')}>
                    <ChevronLeft className="h-4 w-4" />{isMobile ? 'Back' : 'Back to Quotes'}
                  </button>
                ) : (
                  <button className="btn-secondary" onClick={onClose}>Cancel</button>
                )}
                <button
                  className="btn-primary"
                  disabled={newPending.length === 0 || busy}
                  onClick={uploadNewAndGoToForm}
                >
                  {isMobile ? 'Upload' : 'Upload & Continue'}
                </button>
              </div>
            </div>
          )}

          {/* ── Processing ─────────────────────────────────────────────────── */}
          {step === 'processing' && (
            <div className="space-y-4">
              <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                {aiEnabled ? 'Uploading PDFs and running AI extraction…' : 'Uploading PDFs…'}
              </p>
              <div className="space-y-3">
                {slots.filter(s => s.file).map((slot, i) => {
                  const status = slot.error
                    ? 'error'
                    : slot.extracting
                    ? 'extracting'
                    : slot.uploading
                    ? 'uploading'
                    : slot.quote
                    ? 'done'
                    : 'waiting'

                  const statusLabel: Record<string, string> = {
                    waiting: 'Waiting…',
                    uploading: 'Uploading…',
                    extracting: 'AI extracting…',
                    done: aiEnabled ? 'Done' : 'Uploaded',
                    error: slot.error ?? 'Failed',
                  }

                  const statusColor: Record<string, string> = {
                    waiting: 'var(--text-secondary)',
                    uploading: 'var(--brand-primary)',
                    extracting: 'var(--brand-primary)',
                    done: 'var(--success-text, #166534)',
                    error: 'var(--danger-text, #dc2626)',
                  }

                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 14px',
                        borderRadius: 8,
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-muted)',
                      }}
                    >
                      <FileText style={{ width: 18, height: 18, color: 'var(--text-secondary)', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {slot.file!.name}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 500, color: statusColor[status], display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                        {(status === 'uploading' || status === 'extracting') && (
                          <span className="spinner" style={{ width: 12, height: 12, display: 'inline-block' }} />
                        )}
                        {status === 'done' && <Check style={{ width: 13, height: 13 }} />}
                        {statusLabel[status]}
                      </span>
                    </div>
                  )
                })}
              </div>
              {aiEnabled && (
                <p className="form-hint" style={{ margin: 0 }}>
                  Gemini is reading each PDF to extract insurer name, premium, IDV and add-ons. This takes a few seconds per document.
                </p>
              )}
            </div>
          )}

          {/* ── Form tabs ──────────────────────────────────────────────────── */}
          {step === 'form' && (
            <div className="space-y-4">
              {/* Tab bar */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                {slots.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      onClick={() => setActiveTab(i)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px 0 0 6px',
                        border: '1px solid var(--border-color)',
                        borderRight: 'none',
                        background: activeTab === i ? 'var(--brand-primary)' : 'var(--bg-muted)',
                        color: activeTab === i ? '#fff' : 'var(--text-primary)',
                        fontSize: 13,
                        cursor: 'pointer',
                        fontWeight: activeTab === i ? 600 : 400,
                      }}
                    >
                      {s.extracting && (
                        <span className="spinner" style={{ width: 12, height: 12, display: 'inline-block', marginRight: 4 }} />
                      )}
                      {s.form.insurer_name || `Quote ${i + 1}`}
                      {s.error && <AlertTriangle style={{ width: 12, height: 12, marginLeft: 4, color: '#ef4444' }} />}
                      {!s.error && s.quote && slotErrors(s).length > 0 && (
                        <span title={slotErrors(s).join(', ')} style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <AlertTriangle style={{ width: 12, height: 12, marginLeft: 4, color: '#f59e0b' }} />
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => deleteSlot(i)}
                      title="Remove this quote"
                      style={{
                        padding: '4px 6px',
                        borderRadius: '0 6px 6px 0',
                        border: '1px solid var(--border-color)',
                        background: activeTab === i ? 'var(--brand-primary)' : 'var(--bg-muted)',
                        color: activeTab === i ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)',
                        fontSize: 13,
                        cursor: 'pointer',
                        lineHeight: 1,
                      }}
                    >
                      <X style={{ width: 11, height: 11 }} />
                    </button>
                  </div>
                ))}

                {/* Add more button */}
                {slots.length < MAX_FILES && (
                  <button
                    onClick={() => setStep('drop')}
                    title="Add more PDFs"
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      border: '1px dashed var(--border-color)',
                      background: 'transparent',
                      color: 'var(--text-secondary)',
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Plus style={{ width: 13, height: 13 }} /> Add PDF
                  </button>
                )}
              </div>

              {/* Active slot form */}
              {slots[activeTab] && (() => {
                const slot = slots[activeTab]
                const conf = slot.extraction?.confidence

                return (
                  <div className="space-y-3">
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                      File: <strong>{slot.file?.name ?? slot.quote?.pdf_filename ?? '—'}</strong>
                      {slot.uploading && <span className="spinner" style={{ width: 12, height: 12, display: 'inline-block', marginLeft: 6 }} />}
                      {slot.extracting && <span style={{ marginLeft: 8, color: 'var(--brand-primary)', fontSize: 12 }}>AI extracting…</span>}
                    </p>

                    {slot.error && <div className="alert-danger" style={{ fontSize: 13 }}>{slot.error}</div>}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="form-group">
                        <label className="form-label">
                          Insurer Name <span style={{ color: 'var(--danger-text)' }}>*</span>
                          {conf && <ConfidenceBadge score={conf.insurer_name} />}
                        </label>
                        <input
                          type="text"
                          className="input"
                          value={slot.form.insurer_name}
                          onChange={e => updateForm(activeTab, { insurer_name: e.target.value })}
                          placeholder="e.g. HDFC ERGO"
                          style={showValidation && !slot.form.insurer_name.trim() ? { borderColor: 'var(--danger-text)' } : undefined}
                        />
                        {showValidation && !slot.form.insurer_name.trim() && (
                          <span className="form-hint" style={{ color: 'var(--danger-text)' }}>Required</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Policy Type <span style={{ color: 'var(--danger-text)' }}>*</span>
                          {conf && <ConfidenceBadge score={conf.policy_type} />}
                        </label>
                        <select
                          className="input"
                          value={slot.form.policy_type}
                          onChange={e => updateForm(activeTab, { policy_type: e.target.value })}
                          style={showValidation && !slot.form.policy_type ? { borderColor: 'var(--danger-text)' } : undefined}
                        >
                          <option value="">Select…</option>
                          {POLICY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        {showValidation && !slot.form.policy_type && (
                          <span className="form-hint" style={{ color: 'var(--danger-text)' }}>Required</span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="form-group">
                        <label className="form-label">
                          Premium (₹) <span style={{ color: 'var(--danger-text)' }}>*</span>
                          {conf && <ConfidenceBadge score={conf.premium_amount} />}
                        </label>
                        <input
                          type="number"
                          className="input"
                          value={slot.form.premium_amount}
                          onChange={e => updateForm(activeTab, { premium_amount: e.target.value })}
                          placeholder="e.g. 14500"
                          min="0"
                          style={showValidation && (!slot.form.premium_amount || parseFloat(slot.form.premium_amount) <= 0) ? { borderColor: 'var(--danger-text)' } : undefined}
                        />
                        {showValidation && (!slot.form.premium_amount || parseFloat(slot.form.premium_amount) <= 0) && (
                          <span className="form-hint" style={{ color: 'var(--danger-text)' }}>Required</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          IDV (₹) <span style={{ color: 'var(--danger-text)' }}>*</span>
                          {conf && <ConfidenceBadge score={conf.idv} />}
                        </label>
                        <input
                          type="number"
                          className="input"
                          value={slot.form.idv}
                          onChange={e => updateForm(activeTab, { idv: e.target.value })}
                          placeholder="e.g. 350000"
                          min="0"
                          style={showValidation && (!slot.form.idv || parseFloat(slot.form.idv) <= 0) ? { borderColor: 'var(--danger-text)' } : undefined}
                        />
                        {showValidation && (!slot.form.idv || parseFloat(slot.form.idv) <= 0) && (
                          <span className="form-hint" style={{ color: 'var(--danger-text)' }}>Required</span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="form-group">
                        <label className="form-label">
                          OD Premium (₹)
                          {conf && <ConfidenceBadge score={conf.od_premium} />}
                        </label>
                        <input
                          type="number"
                          className="input"
                          value={slot.form.od_premium}
                          onChange={e => updateForm(activeTab, { od_premium: e.target.value })}
                          placeholder="Optional"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          TP Premium (₹)
                          {conf && <ConfidenceBadge score={conf.tp_premium} />}
                        </label>
                        <input
                          type="number"
                          className="input"
                          value={slot.form.tp_premium}
                          onChange={e => updateForm(activeTab, { tp_premium: e.target.value })}
                          placeholder="Optional"
                          min="0"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Add-ons</label>
                      <div className="grid grid-cols-2 gap-y-2">
                        {ADDON_FIELDS.map(({ key, label }) => (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={slot.form[key] as boolean}
                              onChange={e => updateForm(activeTab, { [key]: e.target.checked })}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })()}

              {slots.length === 0 && (
                <div className="empty-state py-6">
                  No quotes yet. <button className="btn-secondary" style={{ marginLeft: 8 }} onClick={() => setStep('drop')}>Add PDFs</button>
                </div>
              )}

              <div className="flex justify-between pt-2" style={{ gap: 8 }}>
                <button className="btn-secondary" onClick={() => setStep('drop')} disabled={busy}>
                  <ChevronLeft className="h-4 w-4" />{isMobile ? 'PDFs' : 'Add More'}
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  {activeTab > 0 && (
                    <button className="btn-secondary" onClick={() => setActiveTab(t => t - 1)}>
                      <ChevronLeft className="h-4 w-4" /> Prev
                    </button>
                  )}
                  {activeTab < slots.length - 1 ? (
                    <button className="btn-primary" onClick={() => setActiveTab(t => t + 1)}>
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      className="btn-primary"
                      onClick={goToPreview}
                      disabled={busy || loadingPreview || !allUploaded || !allSlotsValid}
                      title={!allSlotsValid ? 'Fill in all required fields for each quote before previewing' : undefined}
                    >
                      {loadingPreview ? 'Generating…' : isMobile ? 'Preview' : 'Preview WA Message'}
                    </button>
                  )}
                </div>
              </div>
              {!allSlotsValid && allUploaded && (
                <p style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 6, textAlign: 'right' }}>
                  Complete all required fields (Insurer, Type, Premium, IDV) for each quote tab.
                </p>
              )}
            </div>
          )}

          {/* ── WA Preview ──────────────────────────────────────────────────── */}
          {step === 'preview' && (
            <div className="space-y-4">
              {templateSent ? (
                <div className="alert-success">
                  WhatsApp message sent successfully to the customer. Lead moved to <strong>Quotation Sent</strong>.
                </div>
              ) : (
                <>
                  {slots.length !== comparisonCount && (
                    <div className="alert-warning" style={{ fontSize: 13 }}>
                      <strong>Direct send requires exactly {comparisonCount} quote{comparisonCount !== 1 ? 's' : ''}.</strong>{' '}
                      You have {slots.length}. You can still copy and paste the message below, or go back to adjust.
                    </div>
                  )}
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Copy and paste this message from your WhatsApp Business app, or send it directly via the approved template.
                  </p>
                  <div
                    style={{
                      background: '#dcf8c6',
                      borderRadius: '12px 12px 0 12px',
                      padding: '12px 14px',
                      maxWidth: '480px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      fontSize: 13,
                      color: '#111',
                      lineHeight: 1.6,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                  >
                    {waMessage}
                  </div>
                </>
              )}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: 8, paddingTop: 8 }}>
                {!templateSent && (
                  <button className="btn-secondary" onClick={() => setStep('form')}>
                    <ChevronLeft className="h-4 w-4" />{isMobile ? 'Edit' : 'Edit Quotes'}
                  </button>
                )}
                <div style={{ display: 'flex', gap: 8, marginLeft: templateSent ? 'auto' : undefined }}>
                  {!templateSent && (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={handleCopy}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        className="btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        disabled={sendingTemplate || slots.length !== comparisonCount}
                        title={slots.length !== comparisonCount ? `Requires exactly ${comparisonCount} quotes` : undefined}
                        onClick={async () => {
                          setSendingTemplate(true)
                          try {
                            await leadService.sendQuoteTemplate(leadId)
                            setTemplateSent(true)
                          } catch (err: unknown) {
                            const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to send'
                            alert(msg)
                          } finally {
                            setSendingTemplate(false)
                          }
                        }}
                      >
                        {sendingTemplate ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <Send className="h-4 w-4" />}
                        {sendingTemplate ? 'Sending…' : 'Send via WhatsApp'}
                      </button>
                    </>
                  )}
                  <button className="btn-primary" onClick={() => { onQuotesUploaded(); onClose() }}>
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  )
}
